import React from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useUser } from '@/context/UserContext';
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Button } from "@/components/ui/button";
import { LogOut, Home, Activity, User, Calendar, Flame, Brain } from 'lucide-react';
import { toast } from 'sonner';
import ImageOptimizer from './ImageOptimizer';
import { Badge } from '@/components/ui/badge';

const Header = () => {
  const { user, logout } = useUser();
  const location = useLocation();
  const navigate = useNavigate();

  const isActive = (path: string) => {
    return location.pathname === path;
  };

  const handleLogout = async () => {
    try {
      await logout();
      toast.success("Logged out successfully!");
      navigate('/');
    } catch (error) {
      console.error("Logout failed:", error);
      toast.error("Failed to log out. Please try again.");
    }
  };

  // Updated navigation structure - renamed Calendar/Guide to 30-Day Plan
  const authenticatedNav = [
    { name: 'Home', path: '/integrated-dashboard', icon: Home },
    { name: '30-Day Plan', path: '/calendar', icon: Calendar },
    { 
      name: 'Tracking', 
      path: '/cycle-tracking', 
      icon: Activity
    },
    { name: 'Profile', path: '/profile', icon: User },
    { 
      name: 'Testing', 
      path: '/cycle-tracking?tab=testing', 
      icon: Flame, 
      hotBadge: true 
    },
  ];

  const guestNav = [
    { name: 'Resources', path: '/resources' },
    { name: 'Contact', path: '/contact' },
  ];

  return (
    <header className="bg-white border-b border-hormony-beige/30 py-3 md:py-4">
      <div className="container max-w-6xl flex items-center justify-between">
        <Link to="/" className="flex items-center font-semibold text-xl md:text-2xl text-[#0A5E77]">
          <img
            src="/lovable-uploads/8c455cfd-8e54-4dab-8567-593435f11bfc.png"
            alt="Hormony Logo"
            className="h-10 w-auto mr-2"
          />
        </Link>

        <nav className="hidden md:flex items-center space-x-4 lg:space-x-6">
          {user ? (
            <>
              {authenticatedNav.map((item) => (
                <Link
                  key={item.name}
                  to={item.path}
                  className={`text-hormony-charcoal hover:text-hormony-coral transition-colors duration-200 flex items-center gap-1 ${
                    isActive(item.path) ? 'font-medium text-hormony-coral' : ''
                  }`}
                >
                  <item.icon size={16} />
                  <span className="hidden sm:inline">{item.name}</span>
                  {item.hotBadge && (
                    <Badge className="ml-1 text-[9px] bg-red-500 text-white flex items-center">
                      <Flame className="h-3 w-3 mr-0.5 animate-pulse-soft" />
                      <span className="hidden sm:inline">HOT</span>
                    </Badge>
                  )}
                </Link>
              ))}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" className="h-8 w-8 p-0">
                    <Avatar className="h-8 w-8">
                      <AvatarImage src={user.avatar || "https://github.com/shadcn.png"} alt={user.name} />
                      <AvatarFallback>{user.name?.charAt(0).toUpperCase() || '?'}</AvatarFallback>
                    </Avatar>
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent className="w-56" align="end" forceMount>
                  <DropdownMenuLabel className="font-normal">
                    <div className="flex flex-col space-y-1">
                      <p className="text-sm font-medium leading-none">{user.name}</p>
                      <p className="text-xs leading-none text-muted-foreground">
                        {user.email}
                      </p>
                    </div>
                  </DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={() => navigate('/profile')}>
                    Profile
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate('/integrated-dashboard')}>
                    Dashboard
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate('/symptom-reporting')}>
                    History
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate('/cycle-tracking?tab=testing')} className="flex items-center">
                    Hormone Testing
                    <Badge className="ml-2 text-[9px] bg-red-500 text-white flex items-center">
                      <Flame className="h-3 w-3 mr-0.5 animate-pulse-soft" />
                      HOT
                    </Badge>
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={handleLogout}>
                    <LogOut className="mr-2 h-4 w-4" />
                    Log out
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </>
          ) : (
            <>
              {guestNav.map((item) => (
                <Link
                  key={item.name}
                  to={item.path}
                  className={`text-hormony-charcoal hover:text-hormony-coral transition-colors duration-200 ${
                    isActive(item.path) ? 'font-medium' : ''
                  }`}
                >
                  {item.name}
                </Link>
              ))}
              <Link to="/login" className="text-hormony-charcoal hover:text-hormony-coral transition-colors duration-200">
                Sign In
              </Link>
              <Link to="/waitlist">
                <Button variant="coral" size="sm">Join Waitlist</Button>
              </Link>
            </>
          )}
        </nav>
      </div>
    </header>
  );
};

export default Header;
